import React, { useEffect, useState } from "react";

import { Button } from "components/Graphs/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/Graphs/Dialogue";
import { Input } from "components/Graphs/Input";
import { invitationClient } from "server/devplane/Invitations";
import { toast } from "utils/toast";

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
}

// Matches the shape <local>@<domain>.<tld>; not RFC-strict but rejects obvious typos.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InviteUserDialog({
  open,
  onOpenChange,
  organizationId,
}: InviteUserDialogProps) {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setEmail("");
      setError("");
    }
  }, [open]);

  const submitInvitation = async () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Email is required");
      return;
    }
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError("Enter a valid email address");
      return;
    }
    if (!organizationId) {
      setError("No active organization selected");
      return;
    }

    setIsSubmitting(true);
    setError("");
    try {
      await invitationClient.createInvitation({
        emailAddress: trimmedEmail,
        organizationId,
      });
      toast.success("Invitation created", {
        description: `${trimmedEmail} was invited.`,
      });
      onOpenChange(false);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : "";
      setError(message || "Failed to create invitation");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Invite User</DialogTitle>
          <DialogDescription>
            Send an invitation to join this organization. Invitations expire in
            7 days.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-white mb-1">
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  submitInvitation();
                }
              }}
              placeholder="name@example.com"
            />
          </div>

          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!organizationId}
            isLoading={isSubmitting}
            onClick={submitInvitation}
          >
            Invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
